import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch, ApiError, resetCsrfToken } from '../../lib/api';

export interface User {
  id: number;
  username: string;
  groups: string[];
  avatar: string;
  avatar_config?: Record<string, unknown>;
  isTestAccount?: boolean;
}

export const AUTH_QUERY_KEY = ['auth', 'me'] as const;

export function useAuth() {
  return useQuery<User | null>({
    queryKey: AUTH_QUERY_KEY,
    queryFn: async () => {
      try {
        return await apiFetch<User>('/api/auth/me/');
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) return null;
        throw e;
      }
    },
    staleTime: 30_000,
    retry: false,
  });
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { username: string; password: string }) =>
      apiFetch<User>('/api/auth/login/', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: (user) => {
      qc.setQueryData(AUTH_QUERY_KEY, user);
      // Django's login() rotates the CSRF cookie; clear our cached
      // token so the next unsafe request fetches a fresh one.
      resetCsrfToken();
    },
  });
}

export interface AuthMethods {
  sso: { enabled: boolean; label: string; loginUrl: string };
}

export function useAuthMethods() {
  return useQuery<AuthMethods>({
    queryKey: ['auth', 'methods'],
    queryFn: () => apiFetch<AuthMethods>('/api/auth/methods/'),
    staleTime: Infinity,
    retry: false,
  });
}

export interface MalpediaCheckResult {
  status: 'new' | 'existing';
}

export function useMalpediaCheck() {
  return useMutation({
    mutationFn: (body: { token: string }) =>
      apiFetch<MalpediaCheckResult>('/api/auth/login/malpedia/', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
  });
}

export function useMalpediaComplete() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { token: string; password: string; username?: string }) =>
      apiFetch<User>('/api/auth/login/malpedia/complete/', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: (user) => {
      qc.setQueryData(AUTH_QUERY_KEY, user);
      resetCsrfToken();
    },
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    // SSO sessions get {redirect} back: Keycloak's end-session URL, so the
    // Keycloak login ends too and the next SSO click asks for credentials.
    mutationFn: () =>
      apiFetch<{ redirect?: string } | undefined>('/api/auth/logout/', { method: 'POST' }),
    onSuccess: (resp) => {
      qc.setQueryData(AUTH_QUERY_KEY, null);
      // Server invalidates the session and issues a new CSRF cookie
      // on next request; clear our cached token so the wrapper refetches.
      resetCsrfToken();
      if (resp?.redirect) window.location.assign(resp.redirect);
    },
  });
}
